// useScenarioAlternatives — every neighbour's complete-bill price for ONE
// scenario, read from that scenario's coverage shard, with the drive verdict
// (plausibility gate + saving floors from lib/localSavings) already applied.
//
// Shared by the Scenario Pricing tab's distance tool and the City view's map
// filter; react-query dedupes the shard load across tabs. A neighbour that
// doesn't price the scenario (coverage < 50%, or a price that fails the
// garbage filter) is simply absent — callers render "no price on file",
// never $0.
import { useQuery } from "@tanstack/react-query";
import { titleCaseHospital } from "@/lib/hospitalName";
import { isGarbagePrice } from "@/lib/priceSanity";
import { getCoverageForScenario, loadScenarioCoverageFor, type CoverageRow } from "@/lib/scenarioCoverageStore";
import type { HospitalScenarioRow } from "@/hooks/useHospitalScenarios";
import type { Neighbour } from "@/hooks/useHospitalNeighbours";
import { driveVerdict, type DriveVerdict } from "@/lib/localSavings";

export interface Alternative {
  id: string;
  name: string;
  city: string;
  state: string;
  miles: number;
  price: number;         // blended complete bill (hospital-priced codes + national for the rest)
  coverage: number;
  verdict: DriveVerdict;
  cs: number | null;
}

export function useScenarioAlternatives(
  hospitalId: string,
  scenarioId: string | null,
  picked: HospitalScenarioRow | undefined,
  neighbours: Neighbour[] | null,
) {
  return useQuery({
    queryKey: ["scenario-alternatives", hospitalId, scenarioId],
    enabled: !!scenarioId && !!neighbours && !!picked,
    staleTime: 1000 * 60 * 30,
    queryFn: async () => {
      await loadScenarioCoverageFor(scenarioId!);
      const rows: CoverageRow[] = getCoverageForScenario(scenarioId!);
      const byId = new Map((neighbours || []).map((n) => [n.id, n] as const));
      const national = picked!.nationalCost;
      const here = picked!.blendedPrice;
      const out: Alternative[] = [];
      for (const r of rows) {
        const n = byId.get(r.h);
        if (!n) continue;
        const cp = r.cp ?? 0;
        if (cp < 50) continue;                                   // same qualification bar as the scenario page
        const matched = r.mc ?? 0, total = r.tc ?? 0, np = r.np ?? 0;
        const price = (matched >= total || national <= 0) ? r.hp : r.hp + Math.max(0, national - np);
        const markup = national > 0 ? ((price - national) / national) * 100 : (r.mp ?? 0);
        if (isGarbagePrice(price, national, markup)) continue;
        out.push({ id: r.h, name: titleCaseHospital(n.n) || n.n, city: titleCaseHospital(n.c) || n.c, state: n.s, miles: n.miles, price, coverage: cp, verdict: driveVerdict(here, price, national, n.miles), cs: n.cs ?? null });
      }
      return out;
    },
  });
}
